import * as React from 'react'
import { Slot } from 'primitives/slot'
import { tw } from 'utils/classnames'

type Direction = 'vertical' | 'horizontal'

export interface ScrollShadowProps {
  /** vertical → top + bottom shadows, horizontal → left + right shadows. */
  direction?: Direction
  /** Hide the start-edge shadow until the user scrolls away from the start. */
  hideOnStart?: boolean
  /** When false, skip subtree MutationObserver (e.g. Slate typing inside the scroller). ResizeObserver still runs. */
  watchContentMutations?: boolean
  className?: string
  /** Applied to both shadow overlays. Override the gradient color via `text-*`. */
  shadowClassName?: string
  /** Applied to both shadow overlays when the gradient color comes from a runtime CSS variable. */
  shadowStyle?: React.CSSProperties
  disabled?: boolean
  /** A single element that accepts a ref. Rendered as-is — `ScrollShadow` only attaches a ref. */
  children: React.ReactElement
}

interface EdgeState {
  atStart: boolean
  atEnd: boolean
  canScroll: boolean
}

const INITIAL_STATE: EdgeState = { atStart: true, atEnd: true, canScroll: false }

/**
 * @example
 * ```tsx
 * <ScrollShadow direction="vertical" hideOnStart className="min-h-0 flex-1">
 *   <div className="flex-1 scroller-xs">{content}</div>
 * </ScrollShadow>
 * ```
 */
export const ScrollShadow = React.forwardRef<HTMLDivElement, ScrollShadowProps>(
  function ScrollShadow(
    {
      direction = 'vertical',
      hideOnStart = true,
      watchContentMutations = true,
      className,
      shadowClassName,
      shadowStyle,
      disabled = false,
      children,
    },
    ref,
  ) {
    const scrollerRef = React.useRef<HTMLElement>(null)
    const [state, setState] = React.useState<EdgeState>(INITIAL_STATE)

    // useLayoutEffect so the initial measurement lands before the first paint — avoids a fade-in flash on mount.
    React.useLayoutEffect(() => {
      if (disabled) {
        return
      }
      const el = scrollerRef.current
      if (!el) {
        return
      }

      const isVertical = direction === 'vertical'

      const update = () => {
        const length = isVertical
          ? el.scrollHeight - el.clientHeight
          : el.scrollWidth - el.clientWidth
        const pos = isVertical ? el.scrollTop : el.scrollLeft
        // 1px slack handles fractional-pixel rounding on hi-DPI devices.
        const atStart = pos <= 1
        const canScroll = length > 0
        const atEnd = !canScroll || pos >= length - 1
        setState(prev => {
          if (prev.atStart === atStart && prev.atEnd === atEnd && prev.canScroll === canScroll) {
            return prev
          }
          return { atStart, atEnd, canScroll }
        })
      }

      // Coalesce scroll + ResizeObserver + MutationObserver bursts into one frame.
      let frame = 0
      const scheduleUpdate = () => {
        if (frame !== 0) {
          return
        }
        frame = requestAnimationFrame(() => {
          frame = 0
          update()
        })
      }

      update()
      el.addEventListener('scroll', scheduleUpdate, { passive: true })
      const resizeObserver = new ResizeObserver(scheduleUpdate)
      resizeObserver.observe(el)
      const mutationObserver = watchContentMutations ? new MutationObserver(scheduleUpdate) : null
      mutationObserver?.observe(el, { childList: true, subtree: true })

      return () => {
        if (frame !== 0) {
          cancelAnimationFrame(frame)
        }
        el.removeEventListener('scroll', scheduleUpdate)
        resizeObserver.disconnect()
        mutationObserver?.disconnect()
      }
    }, [direction, disabled, watchContentMutations])

    if (disabled) {
      return children
    }

    const showStart = state.canScroll && !(hideOnStart && state.atStart)
    const showEnd = state.canScroll && !state.atEnd

    const isVertical = direction === 'vertical'

    return (
      <div ref={ref} className={tw('scroller-hover-parent relative isolate', className)}>
        <Slot ref={scrollerRef}>{children}</Slot>
        <div
          aria-hidden
          style={shadowStyle}
          className={tw(
            'pointer-events-none absolute text-gray-1000 transition-opacity duration-200',
            isVertical
              ? 'left-0 right-0 top-0 h-8 bg-scrim-to-b-100'
              : 'bottom-0 left-0 top-0 w-8 bg-scrim-to-r-100',
            showStart ? 'opacity-100' : 'opacity-0',
            shadowClassName,
          )}
        />
        <div
          aria-hidden
          style={shadowStyle}
          className={tw(
            'pointer-events-none absolute text-gray-1000 transition-opacity duration-200',
            isVertical
              ? 'bottom-0 left-0 right-0 h-8 bg-scrim-to-t-100'
              : 'bottom-0 right-0 top-0 w-8 bg-scrim-to-l-100',
            showEnd ? 'opacity-100' : 'opacity-0',
            shadowClassName,
          )}
        />
      </div>
    )
  },
)
